/**
 * Feature Access Service
 *
 * Handles feature-based access control:
 * - Get user's enabled features
 * - Check feature access
 * - Admin functions for managing user access
 */

import { supabase } from './supabaseService';

// =====================================================
// USER FEATURES
// =====================================================

/**
 * Get all features with user's access status
 */
export async function getUserFeatures(userId) {
  const { data, error } = await supabase
    .rpc('get_user_features', { p_user_id: userId });

  if (error) throw error;
  return data;
}

/**
 * Check if user has access to a specific feature
 */
export async function checkFeatureAccess(userId, featureId) {
  const { data, error } = await supabase
    .rpc('check_feature_access', {
      p_user_id: userId,
      p_feature_id: featureId
    });

  if (error) throw error;
  return data;
}

/**
 * Get list of enabled feature IDs for a user
 */
export async function getEnabledFeatureIds(userId) {
  const { data, error } = await supabase
    .from('user_feature_access')
    .select('feature_id')
    .eq('user_id', userId)
    .eq('is_enabled', true);

  if (error) throw error;
  return data.map(row => row.feature_id);
}

/**
 * Get all available features (active ones)
 */
export async function getAvailableFeatures() {
  const { data, error } = await supabase
    .from('features')
    .select('*')
    .eq('is_active', true)
    .order('display_order');

  if (error) throw error;
  return data;
}

// =====================================================
// ADMIN FUNCTIONS
// =====================================================

/**
 * Get all users with their feature access (admin only)
 */
export async function getAllUsersWithFeatures() {
  const { data, error } = await supabase
    .from('admin_user_features')
    .select('*');

  if (error) throw error;
  return data;
}

/**
 * Get user's complete access list (admin only)
 */
export async function getUserAccessList(userId) {
  const { data, error } = await supabase
    .from('user_feature_access')
    .select(`
      *,
      feature:features(*)
    `)
    .eq('user_id', userId);

  if (error) throw error;
  return data;
}

/**
 * Set feature access for a user (admin only)
 */
export async function setFeatureAccess(userId, featureId, enabled, adminId) {
  const { data, error } = await supabase
    .rpc('set_feature_access', {
      p_user_id: userId,
      p_feature_id: featureId,
      p_enabled: enabled,
      p_admin_id: adminId
    });

  if (error) throw error;
  return data;
}

/**
 * Bulk set feature access for a user (admin only)
 */
export async function bulkSetFeatureAccess(userId, featureStates, adminId) {
  const results = await Promise.all(
    Object.entries(featureStates).map(([featureId, enabled]) =>
      setFeatureAccess(userId, featureId, enabled, adminId)
    )
  );
  return results;
}

/**
 * Check if current user is admin
 */
export async function checkIsAdmin(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('is_admin')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data?.is_admin || false;
}

/**
 * Get all profiles for admin panel
 */
export async function getAllProfiles() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, display_name, is_admin, created_at')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// =====================================================
// FEATURE DEFINITIONS
// =====================================================

export const FEATURES = {
  MINDMAP: 'mindmap',
  CASHFLOW: 'cashflow',
  MOODMAP: 'moodmap',
  FITTRACK: 'fittrack'
};

export const FEATURE_INFO = {
  [FEATURES.MINDMAP]: {
    id: 'mindmap',
    name: 'MindMap',
    description: 'Your second brain - knowledge graph',
    icon: 'Brain',
    route: '/mindmap',
    color: 'indigo'
  },
  [FEATURES.CASHFLOW]: {
    id: 'cashflow',
    name: 'CashFlow',
    description: 'Income and expense tracking',
    icon: 'Wallet',
    route: '/cashflow',
    color: 'emerald'
  },
  [FEATURES.MOODMAP]: {
    id: 'moodmap',
    name: 'MoodMap',
    description: 'Daily mood and reflection tracking',
    icon: 'Heart',
    route: '/moodmap',
    color: 'purple'
  },
  [FEATURES.FITTRACK]: {
    id: 'fittrack',
    name: 'FitTrack',
    description: 'Fitness and workout tracking',
    icon: 'Dumbbell',
    route: '/fittrack',
    color: 'orange'
  }
};
